import React from "react";
import { useTheme } from "../../../context/ThemeContext";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

interface BillablePoint {
  x: string;
  y: number;
}

interface BillableHoursCardProps {
  title: string;
  data?: BillablePoint[] | null;
  loading: boolean;
  description?: string;
}

const BillableHoursCard: React.FC<BillableHoursCardProps> = ({
  title,
  data,
  loading,
  description,
}) => {
  const { isDarkMode } = useTheme();

  const cardBase =
    "p-4 rounded-lg shadow-sm w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700";

  return (
    <div className={cardBase}>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
        {title}
      </h3>

      {description && (
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {description}
        </p>
      )}

      {loading ? (
        <div className="mt-5 flex justify-center items-center">
          <svg
            className="animate-spin h-8 w-8 text-purple-600 dark:text-purple-400"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            />
          </svg>
          <span className="ml-2 text-gray-700 dark:text-gray-300">
            Loading Billable Hours...
          </span>
        </div>
      ) : !data || data.length === 0 ? (
        <div className="mt-5 text-center text-gray-500 dark:text-gray-400 italic">
          No billable hours recorded.
        </div>
      ) : (
        <div className="mt-5 w-full h-80 sm:h-96 lg:h-[28rem]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              {/* Grid */}
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={isDarkMode ? "#374151" : "#e5e7eb"}
              />

              {/* Axes */}
              <XAxis
                dataKey="x"
                stroke={isDarkMode ? "#9ca3af" : "#374151"}
              />
              <YAxis
                stroke={isDarkMode ? "#9ca3af" : "#374151"}
              />

              {/* Tooltip */}
              <Tooltip
                contentStyle={{
                  backgroundColor: isDarkMode ? "#111827" : "#ffffff",
                  border: "1px solid",
                  borderColor: isDarkMode ? "#374151" : "#e5e7eb",
                  color: isDarkMode ? "#f9fafb" : "#111827",
                }}
                labelStyle={{
                  color: isDarkMode ? "#f3f4f6" : "#111827",
                }}
              />

              {/* Line */}
              <Line
                type="monotone"
                dataKey="y"
                stroke={isDarkMode ? "#a78bfa" : "rgb(132, 99, 255)"}
                strokeWidth={3}
                dot={{
                  r: 4,
                  fill: isDarkMode ? "#a78bfa" : "rgb(132, 99, 255)",
                }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default BillableHoursCard;